/**
 * Server-side runtime configuration utility for storefront
 * Use this in Server Components and API routes to get runtime configuration
 */

import { AppConfig } from './context/config-context';

// Re-export AppConfig type for use in other files
export type { AppConfig };

/**
 * Get runtime configuration from environment variables (server-side only)
 * This function should only be called in Server Components or API routes
 *
 * NOTE: NEXT_PUBLIC_* variables are NOT used as fallbacks because they are baked into the
 * client bundle at build time with build machine values, not runtime deployment values.
 * Only server-side environment variables should be used for runtime configuration.
 */
let hasLoggedConfig = false;

export function getServerRuntimeConfig(): AppConfig {
  const hostEnv = (process.env.HOST_ENV || process.env.SENTRY_ENV || '')?.toLowerCase();

  const fullConfig = {
    // Environment
    HOST_ENV: hostEnv || '',

    // API Configuration
    API_BASE_URL: process.env.BASE_URL || '',
    MEDUSA_BACKEND_URL: process.env.MEDUSA_BACKEND_URL || process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || '',
    MEDUSA_BACKEND_URL_CLIENT: process.env.MEDUSA_BACKEND_URL_CLIENT || process.env.MEDUSA_BACKEND_URL || process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || '',
    GTM_KEY: process.env.GTM_KEY || '',
    CLIENT_API_KEY: process.env.CLIENT_API_KEY || '',
    STRIPE_KEY: process.env.STRIPE_KEY || '',

    // Search Service Configuration
    SEARCH_SERVICE_URL: process.env.SEARCH_SERVICE_URL || '',
    SEARCH_SERVICE_TOKEN: process.env.SEARCH_SERVICE_TOKEN || '',

    // Google APIs
    GOOGLE_API_KEY: process.env.GOOGLE_API_KEY || '',

    // Feature Flags
    ENABLE_SCHEDULE_DELIVERY: process.env.ENABLE_SCHEDULE_DELIVERY || '',
    ENABLE_GOOGLE_CAPTCHA: process.env.ENABLE_GOOGLE_CAPTCHA || '', // Empty string = check Medusa feature flag
    APP_VERSION: process.env.APP_VERSION || '',
    DISABLE_LOGIN_SIGNUP: process.env.DISABLE_LOGIN_SIGNUP || 'false',
    STRIPE_ENABLE_OTHER_PAY: process.env.STRIPE_ENABLE_OTHER_PAY || 'true',

    // Payment Configuration
    FISERV_PAYMENTJS_URL: process.env.FISERV_PAYMENTJS_URL || '',
    USIO_APP_URL: process.env.USIO_APP_URL || 'https://app.akimbocard.com/api/v2',
    GOOGLE_CAPTCHA_KEY: process.env.GOOGLE_CAPTCHA_KEY || '',
    PAYPAL_CLIENT_ID: process.env.PAYPAL_CLIENT_ID || '',

    // Product Configuration
    VARIANT_CARD_CONFIG: process.env.VARIANT_CARD_CONFIG || '$50',

    // Analytics
    GA_MEASUREMENT_ID: process.env.GA_MEASUREMENT_ID || 'G-3XS3FYSNJX',

    // Medusa Configuration
    MEDUSA_API_KEY: process.env.MEDUSA_API_KEY || '',
    REVALIDATE_WINDOW: process.env.REVALIDATE_WINDOW || '',

    // Search Configuration (Meilisearch/Algolia)
    SEARCH_API_KEY: process.env.SEARCH_API_KEY || 'test_key',
    SEARCH_ENDPOINT: process.env.SEARCH_ENDPOINT || 'http://127.0.0.1:7700',
    SEARCH_INDEX_NAME: process.env.SEARCH_INDEX_NAME || 'products',

    // Contact Information
    CUSTOMER_SERVICE_PHONE_NUMBER: process.env.CUSTOMER_SERVICE_PHONE_NUMBER || '1-855-798-2146',
    GIFT_CARD_MARKET_EMAIL: process.env.GIFT_CARD_MARKET_EMAIL || 'giftcardmarket.com',

    // Service URLs and Tokens
    REDEMPTION_SERVICE_TOKEN: process.env.REDEMPTION_SERVICE_TOKEN || '',
    REDEMPTION_SERVICE_URL: process.env.REDEMPTION_SERVICE_URL || '',

    // Redemption UI URL
    REDEMPTION_UI_URL: process.env.REDEMPTION_UI_URL || '',
    
    // Logging
    ENABLE_VERBOSE_LOGGING: process.env.ENABLE_VERBOSE_LOGGING === 'true' || false,
  };

  // Log config once on startup (server-side only)
  if (!hasLoggedConfig && typeof window === 'undefined') {
    hasLoggedConfig = true;
    console.log('[Storefront Runtime Config] ========== STARTUP CONFIGURATION ==========');
    console.log('[Storefront Runtime Config] Environment:', hostEnv);
    console.log('[Storefront Runtime Config] Full configuration:');

    // Helper function to mask sensitive values showing first 5 chars and length
    const maskValue = (value: string): string => {
      if (!value) return '';
      const prefix = value.substring(0, 5);
      const length = value.length;
      return `${prefix}... (length: ${length})`;
    };

    // Log with sensitive values masked
    const maskedConfig = { ...fullConfig };
    if (maskedConfig.CLIENT_API_KEY) maskedConfig.CLIENT_API_KEY = maskValue(maskedConfig.CLIENT_API_KEY);
    if (maskedConfig.SEARCH_SERVICE_TOKEN) maskedConfig.SEARCH_SERVICE_TOKEN = maskValue(maskedConfig.SEARCH_SERVICE_TOKEN);
    if (maskedConfig.REDEMPTION_SERVICE_TOKEN) maskedConfig.REDEMPTION_SERVICE_TOKEN = maskValue(maskedConfig.REDEMPTION_SERVICE_TOKEN);
    if (maskedConfig.MEDUSA_API_KEY) maskedConfig.MEDUSA_API_KEY = maskValue(maskedConfig.MEDUSA_API_KEY);
    if (maskedConfig.STRIPE_KEY) maskedConfig.STRIPE_KEY = maskValue(maskedConfig.STRIPE_KEY);
    if (maskedConfig.GOOGLE_API_KEY) maskedConfig.GOOGLE_API_KEY = maskValue(maskedConfig.GOOGLE_API_KEY);
    if (maskedConfig.GOOGLE_CAPTCHA_KEY) maskedConfig.GOOGLE_CAPTCHA_KEY = maskValue(maskedConfig.GOOGLE_CAPTCHA_KEY);
    console.log(JSON.stringify(maskedConfig, null, 2));
    console.log('[Storefront Runtime Config] ========== END ==========');
  }

  return fullConfig;
}

/**
 * Get client-safe runtime configuration (excludes server-only secrets)
 * Use this when injecting config into window.__RUNTIME_CONFIG__ or passing to client components
 */
export function getClientSafeRuntimeConfig(): Omit<AppConfig,
  'MEDUSA_BACKEND_URL' | 'SEARCH_SERVICE_TOKEN' | 
  'REDEMPTION_SERVICE_TOKEN' | 'USIO_APP_URL'> {
  const fullConfig = getServerRuntimeConfig();

  // Create client-safe config by destructuring and omitting sensitive properties
  const {
    MEDUSA_BACKEND_URL,
    SEARCH_SERVICE_TOKEN,
    REDEMPTION_SERVICE_TOKEN,
    USIO_APP_URL,
    ...clientSafeConfig
  } = fullConfig;

  // Return config with sensitive properties completely removed
  return {
    ...clientSafeConfig,

    // Keep only client-safe values:
    // MEDUSA_BACKEND_URL_CLIENT - OK (public API Gateway URL for browser)
    // CLIENT_API_KEY - OK (meant for client-side use)
    // SEARCH_SERVICE_URL - OK (public search endpoint)
    // All feature flags - OK (public configuration)
    // Public keys and IDs - OK
  };
}
